import chalk from 'chalk';
import {Command} from 'commander';
import ora from 'ora';
import pLimit from 'p-limit';
import prompts from 'prompts';
import * as z from 'zod';

import {handleError} from '@/utilities/errors';
import {directoryExists} from '@/utilities/files';
import {cloneKogRegistryItem, loadKogRegistry, updateKogTemplate} from '@/utilities/kog';
import {logger} from '@/utilities/logger';

const ComponentsSchema = z.array(z.string()).optional();
const OptionsSchema = z.object({
  override: z.boolean(),
  cwd: z.string(),
  all: z.boolean(),
  limit: z.coerce
    .number()
    .min(1)
    .max(25)
    .nullish()
    .transform(x => x || undefined),
});

const defaultOptions: Options = {
  override: false,
  cwd: process.cwd(),
  all: false,
  limit: 5,
};

export type Components = z.infer<typeof ComponentsSchema>;
export type Options = z.infer<typeof OptionsSchema>;

export const add = new Command()
  .name('add')
  .description('add a Kog component to your project')
  .argument('[components...]', 'the components to add (optional)')
  .option('-o, --override', 'override existing files.', defaultOptions.override)
  .option('-c, --cwd <cwd>', 'the working directory. defaults to the current directory.', defaultOptions.cwd)
  .option('-a, --all', 'add all available components', defaultOptions.all)
  .option('-l, --limit <limit>', 'Request concurrency limit', String(defaultOptions.limit))
  .action(async (components: Components = [], opts: Options = defaultOptions) => {
    try {
      // Get parameters from command.
      const options = OptionsSchema.parse(opts);
      const {override: optionOverride, cwd: optionCurrentWorkingDirectory, all: optionAllComponents} = options;

      const spinner = ora();
      const limit = pLimit(options.limit || 5);

      logger.info('');
      logger.info(`${chalk.green('KOG')} - a CLI tool to ${chalk.green('add')} KOG components to your project.`);
      logger.info('');

      // Check if the working directory exists.
      if ((await directoryExists(optionCurrentWorkingDirectory)) === false) {
        spinner.start().fail(`Validating working directory - does not exist. Please try again.`);
        process.exit(101);
      }

      // Install or update local version of KOG.
      spinner.start('Loading KOG');
      await updateKogTemplate(spinner);

      // Fetch the Kog registry with a list of all components and their dependencies.
      spinner.text = 'Loading KOG registry';
      const {kogRegistry, kogRegistryKeys} = await loadKogRegistry();
      let selectedComponents = (await optionAllComponents)
        ? // Set the selected components to all components if the all flag is set.
          kogRegistryKeys.concat([])
        : // Otherwise, set the selected components to the defined components passed in as parameters.
          kogRegistryKeys.filter(componentName => components.includes(componentName)).concat([]);
      spinner.succeed('Loaded KOG');

      // If no components were passed in as parameters, prompt the user to select components.
      if (!Object.keys(selectedComponents).length) {
        const {components: promptComponents} = await prompts({
          type: 'multiselect',
          name: 'components',
          message: 'Which components would you like to add?',
          hint: 'Space to select. A to toggle all. Enter to submit.',
          instructions: false,
          choices: kogRegistryKeys.map(componentName => ({
            title: componentName,
            value: componentName,
            selected: optionAllComponents ? true : components.includes(componentName),
          })),
        });

        selectedComponents = promptComponents;
      }

      // If no components were selected, exit.
      if (!selectedComponents?.length) {
        spinner.start().fail(`Validating components - none selected. Please try again.`);
        process.exit(201);
      }

      // Filter the Kog registry to only include the selected components.
      const selectedKogRegistry = await kogRegistryKeys
        .filter(componentName => selectedComponents.includes(componentName))
        .reduce((obj, componentName) => ({...obj, [componentName]: kogRegistry[componentName]}), {});

      // If no components were found, exit.
      if (!Object.entries(selectedKogRegistry).length) {
        spinner.start().fail(`Validating components - not found. Please try again.`);
        process.exit(202);
      }

      const promises = Object.entries(selectedKogRegistry).map(([componentName]) =>
        limit(() => cloneKogRegistryItem(componentName, kogRegistry, optionCurrentWorkingDirectory, optionOverride, true))
      );

      await Promise.all(promises);
    } catch (error) {
      handleError(error);
    }
  });
